import React, { useMemo } from "react";
import { Table } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import './pricing.scss';
import NavBar from "../nav-bar/Nav";

const FEATURES = [
  {
    key: 'discoveryReports',
    title: 'SignetScore risk assessments',
    detail: 'External cyber risk assessment reports for fast, and scalable prospect and client risk profiles.',
  },
  {
    key: 'cloudSecReports',
    title: 'Internal cyber risk data sources',
    detail: 'Combine external findings with internal security data from AWS, Azure, GCP, and M365 integrations.',
  },
  {
    key: 'riskscenarios',
    title: 'Exposure/Financial risk scenario models',
    detail: 'Customizable cyber risk quantification models of business exposure.',
  },
  {
    key: 'assessments',
    title: 'Custom assessments',
    detail: 'Configurable questionnaires for everything else. Such as: client needs assessment, insurance readiness evaluation, specific insurance broker forms, or compliance goals',
  },
  {
    key: 'peerdiscovery',
    title: 'Peer discovery',
    detail: 'Identify similar organizations to expand prospecting pipelines and prioritize outreach.',
  },
  {
    key: 'customservices',
    title: 'Customizable Service Offerings',
    detail: 'Ability to add specific service offerings to summary reports.',
  },
  {
    key: 'clientPortfolio',
    title: 'Share access control',
    detail: 'Provide shared access to clients via granular access controls.',
  },
];

function normalizeProduct(value) {
  return (value || '').trim().toLowerCase().replace(/[\s_+-]+/g, '');
}

function resolveAudience(productParam, audienceParam) {
  const audience = normalizeProduct(audienceParam);
  if (audience === 'insurance' || audience === 'cyberinsurance') return 'insurance';
  if (audience === 'msp') return 'msp';

  const product = normalizeProduct(productParam);
  if (!product) return 'msp';

  if (
    product.includes('broker')
    || product.includes('underwriter')
    || product === 'portfoliopremium'
    || product === 'insurance'
  ) {
    return 'insurance';
  }

  return 'msp';
}

function resolveFeaturedPlanId(productParam, audience) {
  const product = normalizeProduct(productParam);
  if (!product) {
    return audience === 'insurance' ? 'planbroker' : 'salesAssist';
  }

  if (
    (product.includes('salesassist') && product.includes('clientview'))
    || product === 'mspbundle'
  ) {
    return 'mspBundle';
  }

  if (
    (product.includes('broker') && product.includes('clientview'))
    || product === 'insurancebundle'
  ) {
    return 'insuranceBundle';
  }

  if (product === 'bundle') {
    return audience === 'insurance' ? 'insuranceBundle' : 'mspBundle';
  }

  if (product.includes('clientviewmanager') || product === 'clientview') {
    return 'clientViewManager';
  }

  if (product.includes('broker')) return 'planbroker';
  if (product.includes('salesassist') || product === 'portfolio') return 'salesAssist';

  return audience === 'insurance' ? 'planbroker' : 'salesAssist';
}

function withFeatured(plans, featuredPlanId) {
  return plans.map((plan) => ({
    ...plan,
    featured: plan.id === featuredPlanId,
  }));
}

function planHeaderClass(plan) {
  return plan.featured
    ? 'comparison-table-plans featured-plan plan-header'
    : 'comparison-table-plans nonfeatured-plan plan-header';
}

function planCellClass(plan, isLast = false) {
  if (plan.featured) {
    return isLast ? 'featured-plan-cell last' : 'featured-plan-cell';
  }
  return isLast ? 'nonfeatured-plan-cell last' : 'nonfeatured-plan-cell';
}

function getClientViewManagerPlan(site) {
  return {
    id: 'clientViewManager',
    name: 'ClientView Manager',
    purpose: 'Client account management',
    price: '$349/mo · 20 client accounts included · $10 per additional account',
    discoveryReports: true,
    cloudSecReports: true,
    riskscenarios: true,
    assessments: true,
    peerdiscovery: false,
    clientPortfolio: true,
    customservices: false,
    route: `${site}/signup?product=${encodeURIComponent('ClientView Manager')}`,
  };
}

function PricingTable({ plans, features, onSelect }) {
  return (
    <section className="pricing-section">
      <Table className="comparison-table">
        <thead className="comparison-table-header">
          <tr>
            <th></th>
            {plans.map((plan) => (
              <th key={plan.id} className={planHeaderClass(plan)}>
                <h5>{plan.name}</h5>
                <p className="plan-purpose">{plan.purpose}</p>
                <p className="plan-price">{plan.price}</p>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {features.map((feature) => (
            <tr key={feature.key}>
              <th className="feature-label">
                <span className="feature-label__title">{feature.title}</span>
                <span className="feature-label__detail">{feature.detail}</span>
              </th>
              {plans.map((plan) => (
                <td key={plan.id} className={planCellClass(plan)}>
                  <span className="material-icons">{plan[feature.key] ? 'done' : 'remove'}</span>
                </td>
              ))}
            </tr>
          ))}
          <tr>
            <th></th>
            {plans.map((plan) => (
              <td key={plan.id} className={planCellClass(plan, true)}>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    onSelect(plan.route);
                  }}
                  className="btn btn-primary btn-block w-100"
                >
                  Select
                </button>
              </td>
            ))}
          </tr>
        </tbody>
      </Table>
    </section>
  );
}

export default function Pricing() {
  const site = process.env.REACT_APP_SITE || 'https://app.signetrisk.com';
  const [searchParams, setSearchParams] = useSearchParams();

  const productParam = searchParams.get('product') || '';
  const audienceParam = searchParams.get('audience') || '';
  const audience = resolveAudience(productParam, audienceParam);
  const featuredPlanId = resolveFeaturedPlanId(productParam, audience);

  const mspPlans = useMemo(() => withFeatured([
    {
      id: 'salesAssist',
      name: 'SalesAssist',
      purpose: 'MSP sales and prospecting',
      price: '$649/mo · 20 scans included · $10 per additional scan',
      discoveryReports: true,
      cloudSecReports: false,
      riskscenarios: true,
      assessments: true,
      peerdiscovery: true,
      clientPortfolio: false,
      customservices: true,
      route: `${site}/signup?product=${encodeURIComponent('SalesAssist')}`,
    },
    getClientViewManagerPlan(site),
    {
      id: 'mspBundle',
      name: 'SalesAssist + ClientView Manager',
      purpose: 'Prospecting and managed clients together',
      price: 'Bundle pricing available · contact us for volume and packaging options',
      discoveryReports: true,
      cloudSecReports: true,
      riskscenarios: true,
      assessments: true,
      peerdiscovery: true,
      clientPortfolio: true,
      customservices: true,
      route: `${site}/signup?product=${encodeURIComponent('SalesAssist,ClientView Manager')}`,
    },
  ], featuredPlanId), [featuredPlanId, site]);

  const insurancePlans = useMemo(() => withFeatured([
    {
      id: 'planbroker',
      name: 'BrokerAssist',
      purpose: 'Cyber insurance sales and prospecting',
      price: '$649/mo · 20 scans included · $10 per additional scan',
      discoveryReports: true,
      cloudSecReports: false,
      riskscenarios: true,
      assessments: true,
      peerdiscovery: true,
      clientPortfolio: false,
      customservices: true,
      route: `${site}/signup?product=${encodeURIComponent('BrokerAssist')}`,
    },
    getClientViewManagerPlan(site),
    {
      id: 'insuranceBundle',
      name: 'BrokerAssist + ClientView Manager',
      purpose: 'Prospecting and managed clients together',
      price: 'Bundle pricing available · contact us for volume and packaging options',
      discoveryReports: true,
      cloudSecReports: true,
      riskscenarios: true,
      assessments: true,
      peerdiscovery: true,
      clientPortfolio: true,
      customservices: true,
      route: `${site}/signup?product=${encodeURIComponent('BrokerAssist,ClientView Manager')}`,
    },
  ], featuredPlanId), [featuredPlanId, site]);

  const handlePlanCheckout = (route) => {
    window.location.href = route;
  };

  const setAudience = (nextAudience) => {
    const next = new URLSearchParams(searchParams);
    next.set('audience', nextAudience);
    // Clear product when switching tabs so the default featured column for that audience applies.
    next.delete('product');
    setSearchParams(next, { replace: true });
  };

  const isMsp = audience === 'msp';

  return (
    <div className="pricing-page">
      <NavBar />
      <div className="container padded-container">
        <div className="pricing-header px-3 py-3 pb-md-4 mx-auto text-center">
          <h1 className="display-4">Pricing</h1>
          <p className="lead">
            All subscriptions are billed monthly on an annual contract. In addition to the services listed below,
            we are happy to provide custom bundles, volume discounts, and whitelabel services. Please contact us at:
            hello@signetrisk.com for pricing.
          </p>

          <div className="pricing-toggle" role="group" aria-label="Pricing audience">
            <button
              type="button"
              className={`pricing-toggle__btn${isMsp ? ' is-active' : ''}`}
              aria-pressed={isMsp}
              onClick={() => setAudience('msp')}
            >
              MSP
            </button>
            <button
              type="button"
              className={`pricing-toggle__btn${!isMsp ? ' is-active' : ''}`}
              aria-pressed={!isMsp}
              onClick={() => setAudience('insurance')}
            >
              Cyber Insurance
            </button>
          </div>
        </div>

        <PricingTable
          plans={isMsp ? mspPlans : insurancePlans}
          features={FEATURES}
          onSelect={handlePlanCheckout}
        />
      </div>
    </div>
  );
}
